export const BAR_PALETTE = [
  'rgba(37, 99, 235, 0.35)',
  'rgba(14, 165, 233, 0.35)',
  'rgba(16, 185, 129, 0.35)',
  'rgba(251, 191, 36, 0.35)',
  'rgba(236, 72, 153, 0.35)',
  'rgba(99, 102, 241, 0.35)',
  'rgba(45, 212, 191, 0.35)',
  'rgba(180, 83, 9, 0.35)',
  'rgba(59, 130, 246, 0.35)',
  'rgba(139, 92, 246, 0.35)',
];

export const PIE_PALETTE = [
  '#BFDBFE',
  '#FDE68A',
  '#FBCFE8',
  '#CCFBF1',
  '#DDD6FE',
  '#FECACA',
  '#E0F2FE',
  '#DCFCE7',
];

// Heatmap gradient endpoints for correlation visualization
// Positive correlations: blue gradient (light to dark)
export const HEATMAP_POSITIVE_START = { r: 239, g: 246, b: 255, a: 0.6 };  // blue-50
export const HEATMAP_POSITIVE_END = { r: 37, g: 99, b: 235, a: 0.95 };     // blue-600

// Negative correlations: red gradient (light to dark)
export const HEATMAP_NEGATIVE_START = { r: 254, g: 242, b: 242, a: 0.6 };  // red-50
export const HEATMAP_NEGATIVE_END = { r: 220, g: 38, b: 38, a: 0.95 };     // red-600

/**
 * Linearly interpolate between two values.
 */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Get a color for a correlation value using a dynamic gradient.
 * Positive values map to blue, negative to red.
 * Uses a blend of sigmoid and linear functions for emphasis around a center point.
 * 
 * @param value - The correlation coefficient (-1 to 1)
 * @param sigmoidWeight - Weight of sigmoid vs linear (0 = pure linear, 1 = pure sigmoid)
 * @param sigmoidSteepness - How steep the sigmoid curve is. Higher = sharper transition.
 * @param sigmoidCenter - The center point of the sigmoid (where the steepest change occurs)
 */
export function getCorrelationColor(
  value: number,
  sigmoidWeight: number = 0.9,
  sigmoidSteepness: number = 20,
  sigmoidCenter: number = 0.25
): string {
  const absValue = Math.min(Math.abs(value), 1);
  
  // Select gradient endpoints based on sign
  const start = value >= 0 ? HEATMAP_POSITIVE_START : HEATMAP_NEGATIVE_START;
  const end = value >= 0 ? HEATMAP_POSITIVE_END : HEATMAP_NEGATIVE_END;
  
  // Sigmoid function centered at sigmoidCenter with configurable steepness
  // sigmoid(x) = 1 / (1 + exp(-k * (x - center)))
  const sigmoid = 1 / (1 + Math.exp(-sigmoidSteepness * (absValue - sigmoidCenter)));
  
  // Normalize sigmoid to 0-1 range (since sigmoid(0) != 0)
  const sigmoidMin = 1 / (1 + Math.exp(-sigmoidSteepness * (0 - sigmoidCenter)));
  const sigmoidMax = 1 / (1 + Math.exp(-sigmoidSteepness * (1 - sigmoidCenter)));
  const sigmoidNormalized = (sigmoid - sigmoidMin) / (sigmoidMax - sigmoidMin);
  
  // Linear function
  const linear = absValue;
  
  // Blend sigmoid and linear based on weight
  const t = sigmoidWeight * sigmoidNormalized + (1 - sigmoidWeight) * linear;
  
  // Interpolate between start and end colors
  const r = Math.round(lerp(start.r, end.r, t));
  const g = Math.round(lerp(start.g, end.g, t));
  const b = Math.round(lerp(start.b, end.b, t));
  const a = lerp(start.a, end.a, t);
  
  return `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`;
}

